import React, { useState, useRef, useEffect, useCallback } from 'react';
import styles from '../../styles/interactive-docs.module.css';
import SandboxWrapper from './SandboxWrapper';
import {
    DrawPolygonIcon,
    DrawLineIcon,
    PinIcon
} from '../../../../components/Icon';

const TOOL_NAMES = {
    polygon: 'Участки и здания',
    line: 'Линии',
    marker: 'Метки'
};

const DEFAULT_OBJECTS = [
    {
        id: 'sample-plot-1',
        type: 'polygon',
        name: 'Тестовый участок №1',
        color: '#2563eb',
        fill: 'rgba(37, 99, 235, 0.2)',
        points: [
            { x: 80, y: 70 },
            { x: 260, y: 50 },
            { x: 290, y: 190 },
            { x: 100, y: 220 }
        ]
    }
];

// Функция для расчёта координат с умной привязкой к углам (Shift)
const getSnappedCoordinates = (rawX, rawY, points, isShift, isPolygon = false) => {
    if (!isShift || points.length === 0) {
        return { x: rawX, y: rawY, isDualSnap: false };
    }

    // Специальная привязка 4-й точки (двойной прямой угол 90° к p2 и p0)
    if (isPolygon && points.length === 3) {
        const p0 = points[0];
        const p1 = points[1];
        const p2 = points[2];
        const rectCorner = {
            x: Math.round(p0.x + p2.x - p1.x),
            y: Math.round(p0.y + p2.y - p1.y)
        };
        const distToCorner = Math.hypot(rawX - rectCorner.x, rawY - rectCorner.y);
        const diagDist = Math.hypot(p0.x - p2.x, p0.y - p2.y);

        if (diagDist > 0 && distToCorner < Math.max(70, diagDist * 0.45)) {
            return { x: rectCorner.x, y: rectCorner.y, isDualSnap: true };
        }
    }

    const prevPt = points[points.length - 1];
    const dx = rawX - prevPt.x;
    const dy = rawY - prevPt.y;
    const dist = Math.hypot(dx, dy);
    if (dist === 0) return { x: rawX, y: rawY, isDualSnap: false };

    const angleRad = Math.atan2(dy, dx);
    const angleDeg = (angleRad * 180) / Math.PI;

    let finalAngleDeg;
    if (points.length >= 2) {
        const p0 = points[points.length - 2];
        const prevDx = prevPt.x - p0.x;
        const prevDy = prevPt.y - p0.y;
        const prevAngleDeg = (Math.atan2(prevDy, prevDx) * 180) / Math.PI;

        let relAngle = angleDeg - prevAngleDeg;
        while (relAngle <= -180) relAngle += 360;
        while (relAngle > 180) relAngle -= 360;

        const snappedRelAngle = Math.round(relAngle / 45) * 45;
        finalAngleDeg = prevAngleDeg + snappedRelAngle;
    } else {
        finalAngleDeg = Math.round(angleDeg / 45) * 45;
    }

    const rad = (finalAngleDeg * Math.PI) / 180;
    return {
        x: Math.round(prevPt.x + dist * Math.cos(rad)),
        y: Math.round(prevPt.y + dist * Math.sin(rad)),
        isDualSnap: false
    };
};

const DrawingSandbox = ({ initialTool = 'polygon' }) => {
    const [activeTool, setActiveTool] = useState('polygon');
    const [objects, setObjects] = useState(DEFAULT_OBJECTS);
    const [currentPoints, setCurrentPoints] = useState([]);
    const [cursorPos, setCursorPos] = useState(null);
    const [isShiftPressed, setIsShiftPressed] = useState(false);
    const svgRef = useRef(null);
    const containerRef = useRef(null);

    // Синхронизация при смене типа инструмента в пропсах
    useEffect(() => {
        if (initialTool) {
            const toolMap = {
                'drawing-plot': 'polygon',
                'drawing-building': 'polygon',
                'drawing-line': 'line',
                'drawing-marker': 'marker',
                'measure-ruler': 'line',
                'measure-area': 'polygon'
            };
            setActiveTool(toolMap[initialTool] || 'polygon');
        }
    }, [initialTool]);

    const finishGeometry = useCallback(() => {
        if (currentPoints.length < 2) {
            setCurrentPoints([]);
            return;
        }

        if (activeTool === 'polygon' && currentPoints.length < 3) {
            setCurrentPoints([]);
            return;
        }

        const newObj = {
            id: `obj-${Date.now()}`,
            type: activeTool,
            name: activeTool === 'line' ? `Линия ${objects.length + 1}` : `Участок ${objects.length + 1}`,
            color: activeTool === 'line' ? '#16a34a' : '#2563eb',
            fill: 'rgba(37, 99, 235, 0.2)',
            points: [...currentPoints]
        };

        setObjects(prev => [...prev, newObj]);
        setCurrentPoints([]);
    }, [activeTool, currentPoints, objects.length]);

    // Обработка горячих клавиш: Shift, Esc, Enter
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Shift') {
                setIsShiftPressed(true);
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                // Отмена последней точки при Esc
                setCurrentPoints(prev => prev.slice(0, -1));
            }
            if (e.key === 'Enter') {
                e.preventDefault();
                finishGeometry();
            }
        };

        const handleKeyUp = (e) => {
            if (e.key === 'Shift') {
                setIsShiftPressed(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, [finishGeometry]);

    const handleReset = () => {
        setObjects(DEFAULT_OBJECTS);
        setCurrentPoints([]);
    };

    const handleCanvasClick = (e) => {
        if (!svgRef.current) return;
        const rect = svgRef.current.getBoundingClientRect();
        const rawX = Math.round(e.clientX - rect.left);
        const rawY = Math.round(e.clientY - rect.top);

        if (activeTool === 'marker') {
            const newMarker = {
                id: `marker-${Date.now()}`,
                type: 'marker',
                name: `Метка ${objects.length + 1}`,
                x: rawX,
                y: rawY
            };
            setObjects(prev => [...prev, newMarker]);
            return;
        }

        const { x, y } = getSnappedCoordinates(rawX, rawY, currentPoints, e.shiftKey || isShiftPressed, activeTool === 'polygon');

        // 1. Повторный клик по последней поставленной точке — завершение построения
        if (currentPoints.length >= 2) {
            const lastPt = currentPoints[currentPoints.length - 1];
            const distToLast = Math.hypot(lastPt.x - rawX, lastPt.y - rawY);
            if (distToLast < 14) {
                finishGeometry();
                return;
            }
        }

        // 2. Клик по начальной стартовой точке — замыкание полигона
        if (currentPoints.length >= 3 && activeTool === 'polygon') {
            const startPt = currentPoints[0];
            const distToStart = Math.hypot(startPt.x - rawX, startPt.y - rawY);
            if (distToStart < 15) {
                finishGeometry();
                return;
            }
        }

        setCurrentPoints(prev => [...prev, { x, y }]);
    };

    const handleDoubleClick = (e) => {
        e.preventDefault();
        finishGeometry();
    };

    const handleMouseMove = (e) => {
        if (!svgRef.current) return;
        const rect = svgRef.current.getBoundingClientRect();
        const rawX = Math.round(e.clientX - rect.left);
        const rawY = Math.round(e.clientY - rect.top);

        const snapped = getSnappedCoordinates(rawX, rawY, currentPoints, e.shiftKey || isShiftPressed, activeTool === 'polygon');
        setCursorPos(snapped);
    };

    const handleMouseLeave = () => {
        setCursorPos(null);
    };

    const toolNameRu = TOOL_NAMES[activeTool] || 'Участки и здания';

    return (
        <SandboxWrapper
            title="Интерактивный тренажёр рисования"
            onReset={handleReset}
        >
            <div ref={containerRef} className={styles.sandboxCanvasArea}>
                {/* Панель инструментов внутри песочницы */}
                <div className={styles.sandboxToolbar}>
                    <button
                        className={`${styles.sandboxToolBtn} ${activeTool === 'polygon' ? styles.sandboxToolBtnActive : ''}`}
                        onClick={() => { setActiveTool('polygon'); setCurrentPoints([]); }}
                        title="Рисовать участки и здания"
                    >
                        <DrawPolygonIcon size={14} />
                        <span>Участки и здания</span>
                    </button>
                    <button
                        className={`${styles.sandboxToolBtn} ${activeTool === 'line' ? styles.sandboxToolBtnActive : ''}`}
                        onClick={() => { setActiveTool('line'); setCurrentPoints([]); }}
                        title="Рисовать линии"
                    >
                        <DrawLineIcon size={14} />
                        <span>Линии</span>
                    </button>
                    <button
                        className={`${styles.sandboxToolBtn} ${activeTool === 'marker' ? styles.sandboxToolBtnActive : ''}`}
                        onClick={() => { setActiveTool('marker'); setCurrentPoints([]); }}
                        title="Ставить метки"
                    >
                        <PinIcon size={14} />
                        <span>Метки</span>
                    </button>
                </div>

                {/* Подсказка для пользователя на русском языке */}
                <div className={styles.sandboxGuidePrompt}>
                    {currentPoints.length === 0 ? (
                        <span>💡 Кликните по холсту, чтобы поставить первую точку инструмента «<strong>{toolNameRu}</strong>».</span>
                    ) : currentPoints.length === 3 && activeTool === 'polygon' ? (
                        <span>
                            🎯 Удерживайте <strong>Shift</strong> для автопривязки 4-го угла (90°). <strong>Enter</strong> или <strong>клик по последней точке</strong> — завершить.
                        </span>
                    ) : (
                        <span>
                            💡 Удерживайте <strong>Shift</strong> для ровных углов (90° и 45°). <strong>Enter</strong> или <strong>клик по последней точке</strong> — завершить, <strong>Esc</strong> — отменить точку.
                        </span>
                    )}
                </div>

                {/* SVG Холст */}
                <svg
                    ref={svgRef}
                    width="100%"
                    height="100%"
                    onClick={handleCanvasClick}
                    onDoubleClick={handleDoubleClick}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                    style={{ display: 'block', background: '#f8fafc' }}
                >
                    <defs>
                        <pattern id="sandbox-grid" width="20" height="20" patternUnits="userSpaceOnUse">
                            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#e2e8f0" strokeWidth="0.8" />
                        </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#sandbox-grid)" />

                    {/* Существующие объекты */}
                    {objects.map(obj => {
                        if (obj.type === 'marker') {
                            return (
                                <g key={obj.id} transform={`translate(${obj.x}, ${obj.y})`} style={{ cursor: 'pointer' }}>
                                    <circle r="14" fill="rgba(37, 99, 235, 0.15)" />
                                    <circle r="6" fill="#2563eb" stroke="#ffffff" strokeWidth="2" />
                                    <text y="20" textAnchor="middle" fontSize="11" fill="#334155" fontWeight="500">
                                        {obj.name}
                                    </text>
                                </g>
                            );
                        }

                        const ptsStr = obj.points.map(p => `${p.x},${p.y}`).join(' ');
                        const isClosed = obj.type === 'polygon';

                        return (
                            <g key={obj.id}>
                                {isClosed ? (
                                    <polygon
                                        points={ptsStr}
                                        fill={obj.fill}
                                        stroke={obj.color}
                                        strokeWidth="2"
                                        strokeDasharray="4,2"
                                    />
                                ) : (
                                    <polyline
                                        points={ptsStr}
                                        fill="none"
                                        stroke={obj.color}
                                        strokeWidth="3"
                                    />
                                )}
                                {obj.points.map((p, idx) => (
                                    <circle
                                        key={idx}
                                        cx={p.x}
                                        cy={p.y}
                                        r="3.5"
                                        fill="#ffffff"
                                        stroke={obj.color}
                                        strokeWidth="1.5"
                                    />
                                ))}
                            </g>
                        );
                    })}

                    {/* Текущее незавершенное построение (чертежный стиль) */}
                    {currentPoints.length > 0 && (
                        <g>
                            {/* Линии двойной привязки для 4-го угла */}
                            {cursorPos && cursorPos.isDualSnap && currentPoints.length === 3 && (
                                <g>
                                    <line
                                        x1={currentPoints[0].x}
                                        y1={currentPoints[0].y}
                                        x2={cursorPos.x}
                                        y2={cursorPos.y}
                                        stroke="#16a34a"
                                        strokeWidth="1.5"
                                        strokeDasharray="2,2"
                                    />
                                    <line
                                        x1={currentPoints[2].x}
                                        y1={currentPoints[2].y}
                                        x2={cursorPos.x}
                                        y2={cursorPos.y}
                                        stroke="#16a34a"
                                        strokeWidth="1.5"
                                        strokeDasharray="2,2"
                                    />
                                </g>
                            )}

                            {/* Направляющая линия до курсора */}
                            {cursorPos && !cursorPos.isDualSnap && (
                                <line
                                    x1={currentPoints[currentPoints.length - 1].x}
                                    y1={currentPoints[currentPoints.length - 1].y}
                                    x2={cursorPos.x}
                                    y2={cursorPos.y}
                                    stroke={isShiftPressed ? "#16a34a" : "#D20C0C"}
                                    strokeWidth="1.8"
                                    strokeDasharray="3,3"
                                />
                            )}
                            {/* Замыкающая линия до стартовой точки */}
                            {activeTool === 'polygon' && currentPoints.length >= 2 && cursorPos && !cursorPos.isDualSnap && (
                                <line
                                    x1={cursorPos.x}
                                    y1={cursorPos.y}
                                    x2={currentPoints[0].x}
                                    y2={currentPoints[0].y}
                                    stroke="rgba(210, 12, 12, 0.35)"
                                    strokeWidth="1"
                                    strokeDasharray="2,2"
                                />
                            )}
                            <polyline
                                points={currentPoints.map(p => `${p.x},${p.y}`).join(' ')}
                                fill={activeTool === 'polygon' ? 'rgba(210, 12, 12, 0.1)' : 'none'}
                                stroke="#D20C0C"
                                strokeWidth="2"
                                strokeDasharray="4,2"
                            />
                            {currentPoints.map((p, idx) => {
                                const isFirst = idx === 0;
                                const isLast = idx === currentPoints.length - 1;
                                if (!isFirst && !isLast) return null;
                                return (
                                    <circle
                                        key={idx}
                                        cx={p.x}
                                        cy={p.y}
                                        r={isFirst ? "5" : "3.5"}
                                        fill="#ffffff"
                                        stroke="#D20C0C"
                                        strokeWidth="2"
                                    />
                                );
                            })}
                        </g>
                    )}
                </svg>
            </div>
        </SandboxWrapper>
    );
};

export default DrawingSandbox;
